import React, { useMemo, useEffect } from 'react';
import { isAuth0Flow as getIsAuth0Flow, setAuth0Provider } from '../../utils/authProvider.js';
import { observer } from 'mobx-react';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingOverlay } from '@mqd/volt-base';
import { pageIsProtected } from './utils/index.js';
import { ProtectedView, UnprotectedView } from './components/index.js';
import { IsLoading, BootstrapSession } from '@mq/volt-amc-container';
import AppLoadingOverlayWrapper from '../AppLoadingOverlayWrapper.js';
import ProgramConfigSetup from '../../utils/program-config/ProgramConfigSetup.js';
import ProgramSelectorSetup from '../program-selector/ProgramSelectorSetup.js';

const Page = ({ gqlApi }) => {
  const protectedPage = useMemo(() => pageIsProtected(), []);
  const auth0 = useAuth0();
  const { loginWithRedirect, isAuthenticated, isLoading } = auth0;

  // Check if this is an Auth0 flow on initial load
  const auth0Flow = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasProviderParam = urlParams.get('auth_provider') === 'auth0';

    // Set auth_provider flag immediately when query param is detected
    // This ensures isAuth0Flow is true on subsequent page loads
    if (hasProviderParam) {
      setAuth0Provider();
    }

    return hasProviderParam || getIsAuth0Flow();
  }, []);

  useEffect(() => {
    if (auth0Flow && !isAuthenticated && !isLoading) {
      // Get the state param which contains the original AMC path
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('state') || '/';
      const loginHint = urlParams.get('login_hint');
      loginWithRedirect({
        appState: { returnTo },
        ...(loginHint && { authorizationParams: { login_hint: loginHint } }),
      });
    }
  }, [loginWithRedirect, isAuthenticated, isLoading, auth0Flow]);

  // Show loading while Auth0 is checking authentication (only for Auth0 flow)
  if (auth0Flow && (isLoading || !isAuthenticated)) {
    return (
      <div style={{ position: 'fixed', zIndex: 9999999 }}>
        <LoadingOverlay active={true}>
          <div style={{ width: '100vw', height: '100vh' }} />
        </LoadingOverlay>
      </div>
    );
  }

  return (
    <IsLoading>
      <AppLoadingOverlayWrapper />
      <ProgramConfigSetup>
        <ProgramSelectorSetup>
          {protectedPage ? (
            <BootstrapSession auth0Data={auth0}>
              <ProtectedView />
            </BootstrapSession>
          ) : (
            <UnprotectedView />
          )}
        </ProgramSelectorSetup>
      </ProgramConfigSetup>
    </IsLoading>
  );
};

export default observer(Page);
